import { createSelector } from "reselect";

import { getSegmentIdsPerLeg } from "@app/helpers/udm/get-segment-ids-per-leg";
import type { SummaryDeparture } from "@app/modules/checkout/components/price-information/departure-passenger-summary";
import { getCheckoutCartPassengersCharge } from "@app/modules/checkout/store/v2/selectors/get-checkout-cart-passengers-charge";
import { getTripsSegments } from "@app/modules/checkout/store/v2/selectors/state-accessors";
import {
  getTripsCities,
  getTripsLocations
} from "@app/modules/results/selectors/state-accessors";
import { getLocationLabel } from "@app/modules/results/selectors/trips/get-city-and-location-labels";
import type { CheckoutCommonReduxState } from "@app/types/checkout-v2-types";
import type {
  NapiV2CartCharges,
  TicketCharges
} from "@app/types/napi-v2-charges-types";

import { getTripLegsByTripId } from "./get-trip-legs";

export const getTripFareSummaryDepartures = createSelector(
  [
    getTripsSegments,
    getTripsLocations,
    getTripsCities,
    getTripLegsByTripId,
    (state: CheckoutCommonReduxState) =>
      getCheckoutCartPassengersCharge(state, {}),
    (
      state,
      _trip_id: string,
      refreshed_cart_charges?: NapiV2CartCharges["items"]
    ) => {
      return {
        state,
        refreshed_cart_charges
      };
    }
  ],
  (
    all_segments,
    trips_locations,
    trips_cities,
    trip_legs,
    charges_per_passenger,
    { state, refreshed_cart_charges }
  ) => {
    const departures_summary: SummaryDeparture[] = [];
    const segment_ids_per_leg_matrix = getSegmentIdsPerLeg(trip_legs);

    segment_ids_per_leg_matrix.forEach((segment_ids, leg_idx) => {
      const trip_segments = segment_ids.map(
        segment_id => all_segments[segment_id]
      );

      const first_segment = trip_segments[0];
      const last_segment = trip_segments[trip_segments.length - 1];

      const { city: origin_city } = getLocationLabel({
        trips_locations,
        trips_cities,
        // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        location_id: first_segment.origin_id
      });

      const { city: destination_city } = getLocationLabel({
        trips_locations,
        trips_cities,
        // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        location_id: last_segment.destination_id
      });

      const refreshed_ticket_charges = refreshed_cart_charges?.filter(
        (charge): charge is TicketCharges => charge.type === "ticket"
      );

      // In case of invalid price Modal, we use the refreshed charges
      const _charges = refreshed_ticket_charges
        ? getCheckoutCartPassengersCharge(state, {}, refreshed_ticket_charges)
        : charges_per_passenger;

      departures_summary.push({
        // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        id: segment_ids[0],
        origin_city: origin_city,
        destination_city: destination_city,
        charges_per_passenger: _charges[leg_idx] || []
      });
    });

    return departures_summary;
  }
);
